// hooks/useTradeErrors.tsx
import { styled } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { SanitisedTradeSummaryRes } from "src/store/features/api/generated";
import { ITradeInput } from "src/store/features/swap.slice";
import { IRefLogic } from "./useTradeRefLogic";
import { TradeError } from "../../components/TradeError";
import { toFriendlyRestrictedMessage } from "src/utils/restrictedCountry";

export const ErrorHereLink = styled(RouterLink)(() => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  textDecoration: "underline",
  "&:hover": {
    opacity: 0.7,
  },
}));

interface useTradeErrorsProps {
  sellInput: ITradeInput;
  buyInput: ITradeInput;
  ref: IRefLogic;
  handleSubmitButtonChange: (change: Record<string, any>) => void;
  userBaseCurrency?: string;
}

const useTradeErrors = (props: useTradeErrorsProps) => {
  const { sellInput, buyInput, handleSubmitButtonChange, userBaseCurrency = "USD" } = props;
  const { previousSellInputRef, previousBuyInputRef } = props.ref;

  const [error, setError] = React.useState<React.ReactNode>(null);
  const [hasError, setHasError] = React.useState<boolean>(false);

  const changeErrorMessage = React.useCallback(
    (type: string): void => {
      switch (type) {
        case "insufficientFunds":
          const currentBalance = +sellInput.maxTotal || "0";
          const requiredAmount = +sellInput.units || "0";
          const deficit = (Number(requiredAmount) - Number(currentBalance)).toFixed(4);

          setError(
            <TradeError
              message={`You currently have ${currentBalance} ${sellInput.code}. Please deposit additional ${deficit} ${sellInput.code} or adjust your order to place your trade.`}
            />,
          );

          handleSubmitButtonChange({
            color: "salmon",
            text: `Deposit ${sellInput.code} here`,
            disabled: false,
          });
          setHasError(true);
          break;

        case "duplicateCode":
          setError(
            <TradeError message="You cannot trade the same coin. Please select a different one." />,
          );
          handleSubmitButtonChange({ color: "salmon", text: "Duplicate Currency", disabled: true });
          setHasError(true);
          break;

        case "belowMinTrade":
          setError(
            <TradeError
              message={`This order is below the 500 ${userBaseCurrency} minimum transaction value, please contact your broker for support.`}
            />,
          );
          handleSubmitButtonChange({
            color: "salmon",
            text: "Below Minimum Order",
            disabled: true,
          });
          setHasError(true);
          break;

        case "reset":
          setError(null);
          setHasError(false);
          handleSubmitButtonChange({ color: "salmon", text: "Place Order", disabled: false });
          break;

        default:
          setError(null);
          handleSubmitButtonChange({ color: "salmon", text: "Place Order", disabled: false });
          setHasError(false);
          break;
      }
    },
    [sellInput, userBaseCurrency, handleSubmitButtonChange],
  );

  const handleErrorFromSummaryResponse = (summary: SanitisedTradeSummaryRes): boolean => {
    if (!summary.error) {
      changeErrorMessage("reset");
      return false;
    }

    handleCustomError(summary.error);
    return true;
  };

  const handleCustomError = (error: string): void => {
    setHasError(true);

    // Backend restricted-country block: surface a more user-friendly message.
    const friendlyRestricted = toFriendlyRestrictedMessage(error);
    if (friendlyRestricted) {
      setError(<TradeError message={friendlyRestricted} />);
      handleSubmitButtonChange((prev: any) => ({ ...prev, disabled: true }));
      return;
    }

    if (error.includes("Not enough funds")) {
      const currentBalance = sellInput.maxTotal || "0";
      const requiredAmount = sellInput.units || "0";
      const deficit = (Number(requiredAmount) - Number(currentBalance)).toFixed(4);

      setError(
        <TradeError
          message={`You currently have ${currentBalance} ${sellInput.code}. Please deposit additional ${deficit} ${sellInput.code} or adjust your order to place your trade.`}
        />,
      );

      handleSubmitButtonChange({
        color: "salmon",
        text: `Deposit ${sellInput.code} here`,
        disabled: false,
      });
    } else {
      setError(<TradeError message={error} />);
      handleSubmitButtonChange((prev: any) => ({ ...prev, disabled: true }));
    }
  };

  React.useEffect((): void => {
    const previousSellUnits = previousSellInputRef.current.units;
    const previousBuyUnits = previousBuyInputRef.current.units;
    const previousSellCode = previousSellInputRef.current.code;
    const previousBuyCode = previousBuyInputRef.current.code;

    // Priority: duplicateCode > insufficientFunds > reset
    if (
      sellInput.code === buyInput.code &&
      sellInput.code !== "Select" &&
      buyInput.code !== "Select"
    ) {
      changeErrorMessage("duplicateCode");
    } else if (
      Number(sellInput.units) > Number(sellInput.maxTotal) &&
      sellInput.code !== "Select"
    ) {
      changeErrorMessage("insufficientFunds");
    } else if (
      sellInput.units !== previousSellUnits ||
      buyInput.units !== previousBuyUnits ||
      sellInput.code !== previousSellCode ||
      buyInput.code !== previousBuyCode
    ) {
      changeErrorMessage("reset");
    }

    /**
     * If Statement here is for a specific case where the user changes sell currency while they have an error for 'insufficientFunds'.
     * This is to reset the error message when the user changes the sell currency. Allowing for a trade summary to be calculated.
     */
    if (sellInput.code !== "Select" && sellInput.code !== previousSellInputRef.current.code) {
      changeErrorMessage("reset");
    }

    // Update refs
    previousSellInputRef.current.units = sellInput.units;
    previousBuyInputRef.current.units = buyInput.units;
    previousSellInputRef.current.code = sellInput.code;
    previousBuyInputRef.current.code = buyInput.code;
  }, [sellInput.units, sellInput.code, sellInput.maxTotal, buyInput.units, buyInput.code]);

  return { error, hasError, changeErrorMessage, handleCustomError, handleErrorFromSummaryResponse };
};

export default useTradeErrors;
